import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import { AuthProfileSerializer } from "@shared/modules/auth/serializer-types";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import GuestCheckout from "@app-contexts/public/shared/checkout/components/guest-checkout/components/GuestCheckout/GuestCheckout/GuestCheckout.web";
import {
  AuthenticationContainer,
  AuthenticationContainerScreen,
} from "@shared/modules/login/components/AuthenticationContainer/AuthenticationContainer.web";
import type { CheckoutLoginSharedProps } from "./CheckoutLogin.props";
import CheckoutLoginContent from "../CheckoutLoginContent/CheckoutLoginContent.web";

export default function CheckoutLogin({
  forceGuestCheckout = false,
  onAuthSuccess,
}: {
  forceGuestCheckout?: boolean;
  onAuthSuccess?: (isGuestCheckout: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation("common");
  const [tab, setTab] = useState<"auth_container" | "guest">("auth_container");
  const publicBasket = usePublicBasket();
  const { paymentVenue, isWaitlist, updateCustomer } = publicBasket;
  const { useRetrieve } = usePublicVenueService();
  const { data: venue } = useRetrieve(paymentVenue);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [
    screenAllowsGuestCheckoutBtnDisplay,
    setScreenAllowsGuestCheckoutBtnDisplay,
  ] = useState(true); // Default true as AuthenticationContainer's default is true

  const queryClient = useQueryClient();
  const onSuccess = useCallback(async () => {
    try {
      const repository = new AuthProfileRepository();
      const queryKeyManager = new QueryKeyManager(repository);
      const queryKeys = queryKeyManager.generateKey(["retrieve"]);

      // Wait for the query to be available
      await queryClient.refetchQueries(queryKeys);
      const userProfileData =
        queryClient.getQueryData<AuthProfileSerializer>(queryKeys);

      if (userProfileData) {
        await updateCustomer({
          id: userProfileData.id,
          name: userProfileData.username,
          email: userProfileData.email,
          phone: userProfileData.phone_number,
        });
        onAuthSuccess?.(false);
      }
    } catch (error) {
      logAppInfo(`Error updating customer profile: ${error}`);
    }
  }, [queryClient, updateCustomer, onAuthSuccess]);

  const updateCustomerForGuestCheckout = useCallback(
    async (customer: { name: string; email: string; phone?: string }) => {
      try {
        setIsAuthorizing(true);
        await updateCustomer(
          { id: undefined, ...customer },
          {
            shouldPersist: true,
          }
        );
        onAuthSuccess?.(true);
      } finally {
        setIsAuthorizing(false);
      }
    },
    [updateCustomer, onAuthSuccess]
  );

  return (
    <Flex w="100%" minH="full">
      {tab === "guest" || forceGuestCheckout ? (
        <>
          <Flex
            w="100%"
            h="100%"
            justifyContent="center"
            p={{ base: "24px 20px", sm: "24px 20px" }}
          >
            <GuestCheckout
              onBackClick={
                forceGuestCheckout
                  ? undefined
                  : () => {
                      setTab("auth_container");
                      UserInteractionLogger.initiateCheckout({
                        additionalParams: {
                          datum: "impression",
                          impression: "authentication_method",
                          method: AuthenticationContainerScreen.LOGIN, // AuthContainer's default screen is `login`
                        },
                        basket: publicBasket,
                      });
                    }
              }
              onConfirmClick={updateCustomerForGuestCheckout}
              isLoading={isAuthorizing}
            />
          </Flex>
        </>
      ) : (
        <>
          <Flex
            direction="column"
            backgroundColor="bg"
            h="100%"
            w="100%"
            alignItems="center"
            p={{ base: "24px 18px", sm: "24px 20px" }}
            gap="md"
          >
            <Box w="100%" maxW="sm">
              <AuthenticationContainer
                onSuccess={onSuccess}
                onScreenChange={({ screen }) => {
                  setScreenAllowsGuestCheckoutBtnDisplay(
                    screen === AuthenticationContainerScreen.LOGIN
                  );

                  UserInteractionLogger.initiateCheckout({
                    additionalParams: {
                      datum: "impression",
                      impression: "authentication_method",
                      method: screen,
                    },
                    basket: publicBasket,
                  });
                }}
                customLoginScreen={(props) => (
                  <CheckoutLoginContent {...props} />
                )}
              />

              {screenAllowsGuestCheckoutBtnDisplay &&
                venue?.allow_guest_checkout &&
                !isWaitlist && (
                  <Track.Click
                    analytics={{
                      label: "continue-as-guest-button",
                      venue_id: venue?.id,
                    }}
                  >
                    <Button
                      variant="outline"
                      maxW="sm"
                      w="100%"
                      h="element.4xl"
                      mt="md"
                      onClick={() => {
                        setTab("guest");
                        UserInteractionLogger.initiateCheckout({
                          additionalParams: {
                            datum: "impression",
                            impression: "authentication_method",
                            method: "guest",
                          },
                          basket: publicBasket,
                        });
                      }}
                      data-testid="continue-as-guest-button"
                    >
                      {t("Continue as guest")}
                    </Button>
                  </Track.Click>
                )}
            </Box>
          </Flex>
        </>
      )}
    </Flex>
  );
}

export type CheckoutLoginProps = CheckoutLoginSharedProps & {
  // Add any web specific props here.
};
